import { FormHelperText, Stack } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { EventDateRange } from '@/apis/eventDateRange';
import { useDeferredRangeDateField } from '@/hooks';
import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { AccordionField } from '@components/DrawerAccordion';
import TemplateCapableDatePicker from '@pages/AdvancedSearchListCreatorPage/customWidgets/templateCapableComponents/TemplateCapableDatePicker';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';

type Props = {
  onChange: (newValue: EventDateRange) => void;
  value: EventDateRange;
  setIsFilterInvalid: (isInvalid: boolean) => void;
  isAdvancedSearch?: boolean;
};

type ErrorType = 'invalidDate' | 'invalidRange';

const ERROR_MESSAGE: Record<ErrorType, string> = {
  invalidDate: 'Please select a valid date',
  invalidRange: 'Please select an attended to date that is after the attended from date',
};

function computeEventInvalidRange(val: EventDateRange): boolean {
  const isStartHolder = isTemplatePlaceholder(val.startDate);
  const isEndHolder = isTemplatePlaceholder(val.endDate);
  return Boolean(
    val.startDate &&
      val.endDate &&
      !isStartHolder &&
      !isEndHolder &&
      (val.startDate as Date) > (val.endDate as Date)
  );
}

const EventDateRangePicker = ({
  value,
  onChange,
  setIsFilterInvalid,
  isAdvancedSearch,
}: Props) => {
  const inSearchTemplate = useContext(SearchTemplatePageContext);

  const { startDate, endDate } = value;

  // Validation Logic
  const [invalidFromDate, setInvalidFromDate] = useState(false);
  const [invalidToDate, setInvalidToDate] = useState(false);

  const invalidDate = invalidFromDate || invalidToDate;
  const invalidRange = computeEventInvalidRange(value);

  const invalidFromRef = useRef(false);
  const invalidToRef = useRef(false);

  const setInvalidFromForPicker = (next: boolean) => {
    invalidFromRef.current = next;
    setInvalidFromDate(next);
  };
  const setInvalidToForPicker = (next: boolean) => {
    invalidToRef.current = next;
    setInvalidToDate(next);
  };

  const reportValidity = (nextValue: EventDateRange) => {
    setIsFilterInvalid(
      invalidFromRef.current || invalidToRef.current || computeEventInvalidRange(nextValue)
    );
  };

  /* eslint-disable react-you-might-not-need-an-effect/no-pass-data-to-parent -- keep Apply disabled when parent replaces value */
  useEffect(() => {
    setIsFilterInvalid(
      invalidFromRef.current || invalidToRef.current || computeEventInvalidRange(value)
    );
  }, [value, setIsFilterInvalid]);

  const fromField = useDeferredRangeDateField({
    value: startDate,
    reportInvalid: (isInvalid) => {
      setInvalidFromForPicker(isInvalid);
      reportValidity(value);
    },
    commit: (newValue) => {
      const next = { ...value, startDate: newValue as Date | TemplatePlaceholder };
      onChange(next);
      reportValidity(next);
    },
  });
  const toField = useDeferredRangeDateField({
    value: endDate,
    reportInvalid: (isInvalid) => {
      setInvalidToForPicker(isInvalid);
      reportValidity(value);
    },
    commit: (newValue) => {
      const next = { ...value, endDate: newValue as Date | TemplatePlaceholder };
      onChange(next);
      reportValidity(next);
    },
  });

  const errorMsg = useMemo(() => {
    if (invalidDate) return ERROR_MESSAGE.invalidDate;
    if (invalidRange) return ERROR_MESSAGE.invalidRange;
    return '';
  }, [invalidDate, invalidRange]);

  return (
    <AccordionField>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            width: isAdvancedSearch ? '70ch' : '100%',
            minWidth: 0,
            justifyContent: 'space-between',
            'label:not(.Mui-focused)': {
              fontSize: '0.85rem',
              paddingTop: '0.15rem',
            },
          }}
        >
          <TemplateCapableDatePicker
            label="Attended from date"
            value={fromField.displayValue}
            isOnTemplatePage={inSearchTemplate}
            templateBuilderDisplayName="Event attendance filter: From date"
            isError={invalidRange || invalidFromDate}
            onError={fromField.handleError}
            onChange={fromField.handleChange}
            slotProps={{ textField: fromField.textFieldSlotProps }}
          />
          <TemplateCapableDatePicker
            label="Attended to date"
            value={toField.displayValue}
            isOnTemplatePage={inSearchTemplate}
            templateBuilderDisplayName="Event attendance filter: To date"
            isError={invalidRange || invalidToDate}
            onError={toField.handleError}
            onChange={toField.handleChange}
            slotProps={{ textField: toField.textFieldSlotProps }}
          />
        </Stack>
        {errorMsg && <FormHelperText className="Mui-error">{errorMsg}</FormHelperText>}
      </LocalizationProvider>
    </AccordionField>
  );
};

export default EventDateRangePicker;
